import { useMemo, useState, useEffect } from 'react';

const useLazyOperation = (snekQuery, operationType) => {
  const type = operationType === 'query' ? 'lazyQuery' : 'lazyMutation';
  const [_trigger, _data] = useMemo(() => snekQuery[type](), []);
  const [data, setData] = useState(_data);
  const [trigger, setTrigger] = useState(() => _trigger);
  const [errors, setErrors] = useState(undefined);
  const [networkError, setNetworkError] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [isSafe, setIsSafe] = useState(false);
  const [shouldRefetch, setShouldRefetch] = useState(false);
  const executeOperation = async () => {
    setIsLoading(true);
    setIsSafe(false);
    try {
      const {
        data,
        errors
      } = await trigger();
      if (errors) {
        setErrors(errors);
      }
      setData(data);
    } catch (e) {
      setNetworkError(e);
    }
    setIsLoading(false);
    setIsSafe(true);
    setShouldRefetch(false);
  };
  const refetch = () => {
    const [newTrigger, newNode] = snekQuery[type]();
    setData(newNode);
    setTrigger(() => newTrigger);
    setShouldRefetch(true);
    setNetworkError(undefined);
    setErrors(undefined);
    setIsLoading(false);
    setIsSafe(false);
  };
  return [executeOperation, {
    data,
    isLoading,
    isSafe,
    errors,
    networkError,
    refetch,
    shouldRefetch
  }];
};

function useLazyQuery(snekQuery) {
  const [executeQuery, result] = useLazyOperation(snekQuery, 'query');
  useEffect(() => {
    if (result.shouldRefetch) {
      executeQuery();
    }
  }, [result.shouldRefetch]);
  return [executeQuery, result];
}

function useQuery(snekQuery) {
  const [executeQuery, result] = useLazyOperation(snekQuery, 'query');
  useEffect(() => {
    executeQuery();
  }, []);
  useEffect(() => {
    if (result.shouldRefetch) {
      executeQuery();
    }
  }, [result.shouldRefetch]);
  return result;
}

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}

function usePagination(sq, cb, nodeCb) {
  const [nodes, setNodes] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [cursor, setCursor] = useState(null);
  const fetchMore = async () => {
    var _data$pageInfo, _data$edges;
    setIsFetching(true);
    const [data] = await sq.query(q => {
      var _res$pageInfo, _res$pageInfo2;
      const cbRtn = cb(q);
      let node;
      let args = {};
      if (Array.isArray(cbRtn)) {
        node = cbRtn[0];
        if (cbRtn[1]) {
          args = cbRtn[1];
        }
      } else {
        node = cbRtn;
      }
      const res = node == null ? void 0 : node(_extends({
        first: 10,
        after: cursor || undefined
      }, args));
      return {
        edges: (res == null ? void 0 : res.edges.map(edge => {
          return {
            node: nodeCb(edge.node)
          };
        })) || [],
        pageInfo: {
          endCursor: (res == null ? void 0 : (_res$pageInfo = res.pageInfo) == null ? void 0 : _res$pageInfo.endCursor) || null,
          hasNextPage: (res == null ? void 0 : (_res$pageInfo2 = res.pageInfo) == null ? void 0 : _res$pageInfo2.hasNextPage) || false
        }
      };
    });
    if (data.pageInfo.endCursor) {
      setCursor(data.pageInfo.endCursor);
    }
    setHasMore(data == null ? void 0 : (_data$pageInfo = data.pageInfo) == null ? void 0 : _data$pageInfo.hasNextPage);
    if ((data == null ? void 0 : (_data$edges = data.edges) == null ? void 0 : _data$edges.length) > 0) {
      setNodes(prevData => {
        return [...(prevData || []), ...data.edges.map(edge => edge.node)];
      });
    }
    if (!isLoaded) {
      setIsLoaded(true);
    }
  };
  const reset = () => {
    setNodes([]);
    setIsLoaded(false);
    setIsFetching(false);
    setHasMore(true);
    setCursor(null);
  };
  useEffect(() => {
    if ((nodes == null ? void 0 : nodes.length) === 0 && isLoaded === false && isFetching === false && hasMore === true && cursor === null) {
      fetchMore();
    }
  }, [nodes, isLoaded, isFetching, hasMore, cursor]);
  return {
    data: nodes || [],
    isLoaded,
    isFetching,
    fetchMore,
    hasMore,
    reset
  };
}

export { useLazyQuery, usePagination, useQuery };
//# sourceMappingURL=index.react-hooks.js.map
